(ns practitest.frontend.components2.grid.common
  (:require
    [clojure.string :as str]
    [practitest.frontend.common.format-utils :as fu]
    [practitest.frontend.components.grid :as oldgrid]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.components.grid.server :as server]
    [practitest.frontend.components2.buttons :as buttons]
    [reagent.core :as reagent]
    [practitest.frontend.components.grid.columns-settings :as oldsettings]
    [practitest.frontend.components.grid.filters :as oldfilters]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.overlay2 :as overlay2]
    [practitest.frontend.components.tooltip :as tooltip]
    [practitest.frontend.common.i18n :refer [get-translation
                                             format-translation]]))

;; Slightly modified grid version

(defonce types-registry (atom {}))

(defn- default-handler
  [{:keys [type] :as column-info} & _]
  (str "Column type " type " is not supported"))

(defn- get-cell-definition
  [type]
  (or (get @types-registry type)
      {:search-fn  (constantly false)
       :compare-fn (constantly 0)
       :text-fn    (fn [{:keys [field]} row]
                     (str (get row field)))
       :header-fn  default-handler
       :cell-fn    default-handler}))

(defn header-fn
  [column-info]
  ((:header-fn (get-cell-definition (:type column-info)))
   column-info))

(defn body-fn
  [column-info row props]
  ((:cell-fn (get-cell-definition (:type column-info)))
   column-info row props))

(defn text-fn
  [column-info row]
  ((:text-fn (get-cell-definition (:type column-info)))
   column-info row))

(defn compare-fn
  [column-info direction cell-a cell-b]
  ((:compare-fn (get-cell-definition (:type column-info)))
   column-info direction cell-a cell-b))

(defn search-fn
  [column-info cell substring]
  ((:search-fn (get-cell-definition (:type column-info)))
   column-info cell substring))

;; Use text representation for sorting by default
(defn default-compare-fn
  [{:keys [field type] :as column-info} direction row-a row-b]
  (let [text-a (some-> (text-fn column-info row-a) str str/lower-case)
        text-b (some-> (text-fn column-info row-b) str str/lower-case)]
    (oldgrid/nulls-last-sort direction
                             text-a
                             text-b)))

(defn default-search-fn
  [{:keys [field type] :as column-info} row search-text]
  (fu/substring-search? (text-fn column-info row) search-text))

;; Registers new cell type with required functions
;; cell-fn   -> (fn [column-info row tableprops]) -> reagent view for cell
;; header-fn -> (fn [column-info])     -> reagent view for header
;; text-fn   -> (fn [column-info row]) -> string - default search and sorting
;; optional stuff:
;; search-fn -> (fn [column-info row substring]) -> true / false
;; compare-fn   -> (fn [column-info direction row-a row-b]) -> -1 / 0 / 1
(defn register-field-type!
  ([type header-fn text-fn cell-fn]
   (register-field-type! type header-fn text-fn cell-fn default-search-fn default-compare-fn))
  ([type header-fn text-fn cell-fn search-fn compare-fn]
   (swap! types-registry
          assoc type
          {:search-fn  search-fn
           :compare-fn compare-fn
           :text-fn    text-fn
           :header-fn  header-fn
           :cell-fn    cell-fn})))

(def mixpanel-grid-action "Grid Actions")

(defn header-title [column]
  (str (or (:header column)
           (:field column))))

(def default-sort [:display_id :desc])

(defn has-any-filters?
  [grid-state]
  (not (empty? (:autofilters-state @grid-state))))

(defn has-any-sorting?
  [grid-state]
  (let [sort-settings (:sort-settings @grid-state)]
    (and sort-settings
         (not= sort-settings default-sort))))


(defn reset-grid-filter-component [{:keys [sort-order-save
                                           local-state-updating
                                           full-data?] :as grid-props}]
  (let [has-sorting? (has-any-sorting? local-state-updating)
        has-filters? (has-any-filters? local-state-updating)]
    [tooltip/mux-tooltip
     {:position "top"}
     (get-translation "grid.button.reset" "Reset Sorting and Filters")
     (fn [props]
       (let [disabled? (and (not has-sorting?)
                            (not has-filters?))]
         [:span.data-grid-header-button
          (assoc props
            :class (dom/classes-pairs-v :disabled disabled?)
            :on-click (fn [e]
                        (when-not disabled?
                          (mixpanel/track mixpanel-grid-action {"Action" "Reset"})
                          (swap! local-state-updating dissoc :sort-settings :autofilters-state)

                          (when sort-order-save
                            (sort-order-save nil))
                          ;; Saved here
                          (oldfilters/on-filter-changed
                            (if full-data?
                              (dissoc grid-props :on-query-changed)
                              grid-props)))))
          [icons/mux-refresh]]))]))

(defn existing-instances-warning-note [{:keys [selected-tests
                                               added-tests
                                               data
                                               all-selected] :as grid-props}]
  (let [has-selected-tests? (> (count selected-tests) 0)
        has-added-tests? (> (count added-tests) 0)]
    (when has-selected-tests?
      (let [;; Map selected test IDs to their display IDs
            selected-display-ids (when data
                                   (set (keep (fn [test]
                                                (when (contains? selected-tests (:id test))
                                                  (:display_id test)))
                                              data)))
            selected-existing-test? (if selected-display-ids
                                       (some #(contains? added-tests %) selected-display-ids)
                                       ;; Fallback to old behavior if data not available
                                       (some #(contains? selected-tests %) added-tests))]
        (if (or selected-existing-test?
                (and has-added-tests? all-selected))
          [:span
           [:b "Note:"] " you selected tests that already exist in your testset"])))))

(defn filter-control [_ _]
  (let [filter-state (reagent/atom {})
        input-ref (atom nil)]
    (fn [{:keys [local-state-updating
                 get-field-props
                 table-container-ref
                 full-data?]
          :as   grid-props}
         column]
      (let [field (:field column)
            show? (:show? @filter-state)
            hide! #(swap! filter-state assoc :show? false)
            existing-value (get (:autofilters-state @local-state-updating) field)
            toggle! (fn []
                      (swap! filter-state (fn [s]
                                            (-> s
                                                (update :show? not)
                                                (assoc :value existing-value))))

                      (when-let [node @input-ref]
                        ;; Autofocus implementation
                        ;; Oldfilters style autofocus doesn't work in the same way,
                        ;; because new popup implementation mounts form control in invisible state
                        ;; after-render is because input will be redrawn in the next frame
                        (when (:show? @filter-state)
                          (reagent/after-render #(.focus node)))))

            has-filters? (some? existing-value)

            apply-filter (fn [new-filter]
                           (if (nil? new-filter)
                             ;; Clear filter
                             (swap! local-state-updating update :autofilters-state dissoc field)
                             ;; Enable filter
                             (swap! local-state-updating assoc-in [:autofilters-state field] new-filter))

                           ;; Legacy mode
                           (oldfilters/on-filter-changed
                             (if full-data?
                               (dissoc grid-props :on-query-changed)
                               grid-props)))]
        [overlay2/floating-ui-overlay
         {:placement "bottom"
          :shift? true
          :show? show?
          :on-request-close hide!
          :container table-container-ref}
         [:span.filtering-span
          {:class    (dom/classes-pairs-v :active has-filters?)
           :on-click (fn [e]
                       (mixpanel/track mixpanel-grid-action {"Action" "Filter"})

                       (toggle!))}
          (if has-filters?
            [icons/mux-grid-filter-active]
            [icons/mux-grid-filter])]
         ;; Copy-pasted from oldfilters mostly
         [:div.column-settings-popover
          [:div.popover-header
           (let [header-fn (oldfilters/filter-header-fn column)]
             [header-fn {:column column
                         :value (:value @filter-state)}])]
          [:div.popover-body.autofilter-popover
           {:on-key-down (fn [e]
                           ;; Enter to apply filter
                           (when (= (.-keyCode e) 13)
                             (apply-filter (:value @filter-state))
                             (hide!)))}

           (let [view-fn (oldfilters/filter-view-fn column)]
             [view-fn {:value           (:value @filter-state)
                       :column          column
                       ;; Store ref for "autofocus" feature
                       :input-ref       input-ref
                       :get-field-props get-field-props
                       :on-change       (fn [new-value]
                                          (swap! filter-state assoc :value new-value))}])]
                       ;; TODO: enable/disable ?
                       ;:on-apply-filter apply-filter

          [:div.popover-footer
           {:style {:display "flex"}}
           [:div.filter-apply-buttons.btn-group.ms-auto
            [buttons/button {:on-click (fn [e]
                                         (hide!))
                             :text (get-translation "generic.button.cancel" "Cancel")
                             :primary? false}]
            [buttons/button {:on-click (fn [e]
                                         (apply-filter (:value @filter-state))
                                         (hide!))
                             :text (get-translation "generic.button.apply" "Apply")
                             :primary? true}]]]]]))))

(defn extract-error-message
  [raw-message]
  (let [extracted (if-let [matches (re-find #"\[(.*)\]" raw-message)]
                    (second matches)
                    raw-message)
        cleaned (-> extracted
                    (str/replace #"^\"" "")
                    (str/replace #"\"$" ""))]
    cleaned))
